import { elements, categoryLabels, type Element } from "@/data/elements";

/**
 * Periodic Table Speed Quiz - pure question-generation logic.
 *
 * The quiz draws short, single-answer prompts that always have exactly one
 * correct element from the 118-element pool. Each prompt has 4 multiple-choice
 * options drawn from the same period/category neighborhood when possible so
 * distractors aren't trivially obvious.
 *
 * Question kinds:
 *  - "symbol-to-name"   : "Which element has the symbol Fe?"
 *  - "name-to-symbol"   : "What is the symbol of Potassium?"
 *  - "number-to-name"   : "Which element has atomic number 17?"
 *  - "category-to-name" : "Which of these is a noble gas?"
 *  - "group-to-name"    : "Which element is in group 1, period 3?"
 *
 * Difficulty scales by restricting the pool:
 *  - easy   : Z ≤ 20 (first 20 elements)
 *  - normal : Z ≤ 56 (through Barium)
 *  - hard   : full pool of all 118
 */

export type QuizDifficulty = "easy" | "normal" | "hard";

export type QuizQuestionKind =
  | "symbol-to-name"
  | "name-to-symbol"
  | "number-to-name"
  | "category-to-name"
  | "group-to-name";

export interface QuizQuestion {
  /** Stable id for de-duping within a session. */
  id: string;
  kind: QuizQuestionKind;
  prompt: string;
  /** Four display labels - exactly one is correct. */
  choices: [string, string, string, string];
  correctIndex: 0 | 1 | 2 | 3;
  /** Short post-answer explanation. */
  explanation: string;
  /** Longer pedagogical reasoning shown in the "Why this is correct" disclosure. */
  whyCorrect: string;
}

export const DIFFICULTY_LABEL: Record<QuizDifficulty, string> = {
  easy: "Easy (Z ≤ 20)",
  normal: "Normal (Z ≤ 56)",
  hard: "Hard (all 118)",
};

/** Scoring constants - tuned so a perfect 10-question solo run feels rewarding. */
export const QUIZ_TURN_SECONDS = 12;
export const FAST_THRESHOLD_S = 4;
export const POINTS_FAST = 15;
export const POINTS_SLOW = 8;
export const POINTS_WRONG = 0;
export const POINTS_TIMEOUT = 0;

const poolFor = (d: QuizDifficulty): Element[] => {
  if (d === "easy") return elements.filter((e) => e.number <= 20);
  if (d === "normal") return elements.filter((e) => e.number <= 56);
  return elements;
};

function pickRandom<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/** Sample without replacement, return up to `count` items. */
function sampleN<T>(rng: () => number, arr: T[], count: number): T[] {
  const copy = arr.slice();
  const out: T[] = [];
  while (copy.length > 0 && out.length < count) {
    const i = Math.floor(rng() * copy.length);
    out.push(copy.splice(i, 1)[0]);
  }
  return out;
}

/** Shuffle an array of 4 in place and return the new index of `correct`. */
function shuffleWithIndex<T>(rng: () => number, arr: T[], correctValue: T): { items: T[]; index: 0 | 1 | 2 | 3 } {
  const indexed = arr.map((v, i) => ({ v, i }));
  for (let i = indexed.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
  }
  const items = indexed.map((x) => x.v);
  const idx = items.indexOf(correctValue);
  return { items, index: idx as 0 | 1 | 2 | 3 };
}

/**
 * Build a single quiz question. Tries to honour `excludeIds`; falls back to
 * the unfiltered pool if every candidate would be excluded (so long sessions
 * never deadlock).
 */
export function generateQuestion(
  rng: () => number,
  difficulty: QuizDifficulty,
  excludeIds: ReadonlySet<string>,
): QuizQuestion {
  const pool = poolFor(difficulty);
  const KINDS: QuizQuestionKind[] = [
    "symbol-to-name",
    "name-to-symbol",
    "number-to-name",
    "category-to-name",
    "group-to-name",
  ];

  // Try a few times to avoid duplicates, then give up.
  for (let attempt = 0; attempt < 8; attempt++) {
    const kind = pickRandom(rng, KINDS);
    const target = pickRandom(rng, pool);
    const id = `${kind}:${target.number}`;
    if (excludeIds.has(id) && attempt < 7) continue;

    const distractors = sampleN(
      rng,
      pool.filter((e) => e.number !== target.number),
      8,
    );

    const q = buildQuestion(rng, kind, target, distractors, pool, id);
    if (q) return q;
  }

  // Last resort: any pool element, symbol-to-name.
  const target = pickRandom(rng, pool);
  const distractors = sampleN(rng, pool.filter((e) => e.number !== target.number), 3);
  return buildQuestion(rng, "symbol-to-name", target, distractors, pool, `symbol-to-name:${target.number}`)!;
}

function buildQuestion(
  rng: () => number,
  kind: QuizQuestionKind,
  target: Element,
  distractorsPool: Element[],
  fullPool: Element[],
  id: string,
): QuizQuestion | null {
  switch (kind) {
    case "symbol-to-name": {
      const distractors = distractorsPool.slice(0, 3).map((e) => e.name);
      const { items, index } = shuffleWithIndex(rng, [target.name, ...distractors], target.name);
      return {
        id,
        kind,
        prompt: `Which element has the symbol ${target.symbol}?`,
        choices: items as [string, string, string, string],
        correctIndex: index,
        explanation: `${target.symbol} is ${target.name} (atomic number ${target.number}).`,
        whyCorrect: `${target.symbol} is the IUPAC symbol for ${target.name}, atomic number ${target.number} - found in period ${target.period}${target.group ? `, group ${target.group}` : ""}. It's classified as a ${categoryLabels[target.category].toLowerCase()}. Symbols are usually the first one or two letters of the element's Latin or English name (e.g. Fe from "ferrum", Na from "natrium").`,
      };
    }
    case "name-to-symbol": {
      // Distractor symbols should be visually plausible (similar length).
      const distractors = distractorsPool.slice(0, 3).map((e) => e.symbol);
      const { items, index } = shuffleWithIndex(rng, [target.symbol, ...distractors], target.symbol);
      return {
        id,
        kind,
        prompt: `What is the chemical symbol for ${target.name}?`,
        choices: items as [string, string, string, string],
        correctIndex: index,
        explanation: `${target.name}'s symbol is ${target.symbol}.`,
        whyCorrect: `${target.name} (atomic number ${target.number}) uses the symbol ${target.symbol}. The first letter is always capitalised; the second (if present) is always lowercase. Watch out for symbols based on Latin names - ${target.symbol} for ${target.name} is one to memorise.`,
      };
    }
    case "number-to-name": {
      const distractors = distractorsPool.slice(0, 3).map((e) => e.name);
      const { items, index } = shuffleWithIndex(rng, [target.name, ...distractors], target.name);
      return {
        id,
        kind,
        prompt: `Which element has atomic number ${target.number}?`,
        choices: items as [string, string, string, string],
        correctIndex: index,
        explanation: `Atomic number ${target.number} is ${target.name} (${target.symbol}).`,
        whyCorrect: `The atomic number is the count of protons in the nucleus. ${target.number} protons defines ${target.name} (${target.symbol}). It sits in period ${target.period}${target.group ? `, group ${target.group}` : ""} of the periodic table - a ${categoryLabels[target.category].toLowerCase()}.`,
      };
    }
    case "category-to-name": {
      // "Which is a <category>?" - three distractors must be from a DIFFERENT category.
      if (target.category === "unknown") return null;
      const wrongCategory = fullPool.filter(
        (e) => e.category !== target.category && e.category !== "unknown",
      );
      const distractors = sampleN(rng, wrongCategory, 3).map((e) => e.name);
      if (distractors.length < 3) return null;
      const { items, index } = shuffleWithIndex(rng, [target.name, ...distractors], target.name);
      return {
        id,
        kind,
        prompt: `Which of these is a ${categoryLabels[target.category].toLowerCase()}?`,
        choices: items as [string, string, string, string],
        correctIndex: index,
        explanation: `${target.name} (${target.symbol}) is classified as a ${categoryLabels[target.category].toLowerCase()}.`,
        whyCorrect: `${target.name} (${target.symbol}) belongs to the ${categoryLabels[target.category].toLowerCase()} family. ${categoryHint(target.category)} It sits in period ${target.period}${target.group ? `, group ${target.group}` : ""}.`,
      };
    }
    case "group-to-name": {
      if (target.group === null) return null;
      // Distractors share the period when possible - visually similar prompt.
      const samePeriod = fullPool.filter(
        (e) => e.period === target.period && e.number !== target.number,
      );
      const distractors = sampleN(rng, samePeriod.length >= 3 ? samePeriod : fullPool.filter((e) => e.number !== target.number), 3).map(
        (e) => e.name,
      );
      const { items, index } = shuffleWithIndex(rng, [target.name, ...distractors], target.name);
      return {
        id,
        kind,
        prompt: `Which element sits in group ${target.group}, period ${target.period}?`,
        choices: items as [string, string, string, string],
        correctIndex: index,
        explanation: `${target.name} (${target.symbol}) is in group ${target.group}, period ${target.period}.`,
        whyCorrect: `Group tells you the column (and often the number of valence electrons); period tells you the row (and the highest occupied shell). Group ${target.group}, period ${target.period} pinpoints ${target.name} (${target.symbol}, atomic number ${target.number}) - a ${categoryLabels[target.category].toLowerCase()}.`,
      };
    }
  }
}

/** Short pedagogical hint about a category, used inside whyCorrect strings. */
function categoryHint(category: Element["category"]): string {
  switch (category) {
    case "alkali-metal":
      return "Alkali metals (group 1) are soft, very reactive, and form +1 ions.";
    case "alkaline-earth":
      return "Alkaline earth metals (group 2) are reactive metals that form +2 ions.";
    case "transition-metal":
      return "Transition metals (d-block) often have multiple oxidation states and form coloured compounds.";
    case "post-transition-metal":
      return "Post-transition metals are softer, lower-melting metals on the right of the d-block.";
    case "metalloid":
      return "Metalloids straddle the metal/nonmetal boundary and behave as semiconductors.";
    case "nonmetal":
      return "Nonmetals are poor conductors and tend to gain electrons in reactions.";
    case "halogen":
      return "Halogens (group 17) are reactive nonmetals that form −1 ions and diatomic molecules.";
    case "noble-gas":
      return "Noble gases (group 18) have full valence shells, so they're chemically inert under normal conditions.";
    case "lanthanide":
      return "Lanthanides are f-block metals (atomic numbers 57–71) with very similar chemical properties.";
    case "actinide":
      return "Actinides are f-block metals (atomic numbers 89–103); most are radioactive and synthetic.";
    case "unknown":
    default:
      return "Its category isn't firmly established - many superheavy elements are predicted by extrapolation.";
  }
}

/** Resolve an answer attempt into an awarded point value. */
export function scoreAnswer(
  chosenIndex: number | null,
  question: QuizQuestion,
  elapsedMs: number,
): { points: number; outcome: "fast" | "slow" | "wrong" | "timeout" } {
  if (chosenIndex === null) return { points: POINTS_TIMEOUT, outcome: "timeout" };
  if (chosenIndex !== question.correctIndex) return { points: POINTS_WRONG, outcome: "wrong" };
  if (elapsedMs <= FAST_THRESHOLD_S * 1000) return { points: POINTS_FAST, outcome: "fast" };
  return { points: POINTS_SLOW, outcome: "slow" };
}
